import { useEffect, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    Typography,
} from "@mui/material";
import { fetchHubLoginUrl, fetchHubStatus, HubStatus } from "../../services/hub-settings";
import { translate } from "../../translate/translate";
import { savePendingRedirect } from "../../utils/post-login-redirect";
import { destinationWhenHubUrlUnavailable } from "../../utils/redirect-login-utils";

type SessionExpiredDialogProps = {
    open: boolean;
};

const SessionExpiredDialogComponent = ({ open }: SessionExpiredDialogProps) => {
    const [status, setStatus] = useState<HubStatus | null>(null);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;

        fetchHubStatus().then((result) => {
            if (!cancelled) setStatus(result);
        });

        return () => {
            cancelled = true;
        };
    }, [open]);

    // Without a configured Hub there is nothing to be "down": the local
    // form is the normal way in, so no Hub wording is shown at all.
    const hubDown = status?.hub_configured !== false && status?.hub_online === false;
    const localFallbackAvailable = hubDown && !!status?.local_fallback_available;

    const goToLocalLogin = () => {
        savePendingRedirect(`${window.location.pathname}${window.location.search}`);
        window.location.assign("/login");
    };

    const openHubLogin = () => {
        // Opened synchronously, inside the click handler, so the browser
        // doesn't treat it as an unrequested popup: the URL is only known
        // after the async fetch below, but the tab itself must be opened
        // right now.
        const newTab = window.open("", "_blank");
        savePendingRedirect(`${window.location.pathname}${window.location.search}`);

        fetchHubLoginUrl().then(async (loginUrl) => {
            const destination = loginUrl || (await destinationWhenHubUrlUnavailable());
            if (newTab) {
                newTab.location.href = destination;
            } else {
                // Popup was blocked: navigating the current tab still
                // returns to the saved URL after login.
                window.location.href = destination;
            }
        });
    };

    const handleLoginAgain = async () => {
        // Status not known yet (or its request failed): ask again before
        // choosing between the Hub and the local form.
        const current = status ?? (await fetchHubStatus());
        if (current?.hub_configured === false) {
            goToLocalLogin();
            return;
        }
        openHubLogin();
    };

    return (
        <Dialog open={open} disableEscapeKeyDown>
            <DialogTitle>{translate.t("session_expired_title")}</DialogTitle>
            <DialogContent>
                <Typography>{translate.t("session_expired_message")}</Typography>
                {hubDown && (
                    <Typography color="warning.main" mt={2}>
                        {translate.t("hub_unavailable_message")}
                    </Typography>
                )}
                {localFallbackAvailable && (
                    <Typography mt={2}>
                        <Link
                            href="/login"
                            onClick={(event) => {
                                event.preventDefault();
                                goToLocalLogin();
                            }}>
                            {translate.t("local_fallback_login_link")}
                        </Link>
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleLoginAgain}
                    variant="contained"
                    color="primary">
                    {hubDown ? translate.t("retry") : translate.t("login_again")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SessionExpiredDialogComponent;
